<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCurrency } from 'dashboard/composables/useCurrency';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import CreateNewCompanyDialog from 'dashboard/components-next/Companies/CompaniesForm/CreateNewCompanyDialog.vue';

import ContactAPI from 'dashboard/api/contacts';
import CompanyAPI from 'dashboard/api/companies';
import ConversationAPI from 'dashboard/api/conversations';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import DealAPI from 'dashboard/api/deals';

const props = defineProps({
  currentFunnel: {
    type: Object,
    default: null,
  },
  funnels: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['created', 'close']);

const store = useStore();
const { t, locale } = useI18n();
const { currency, currencySymbol } = useCurrency();

// Convert Vue i18n locale (pt_BR) to BCP 47 format (pt-BR) for toLocaleString
const browserLocale = computed(() => locale.value.replace('_', '-'));

// Refs
const dialogRef = ref(null);
const createContactDialogRef = ref(null);
const createCompanyDialogRef = ref(null);

// Estado do formulário
const isCreatingLead = ref(false);

// Busca unificada
const searchQuery = ref('');
const isSearching = ref(false);
const showDropdown = ref(false);
const searchResults = ref({ contacts: [], companies: [] });

// Seleções
const selectedContact = ref(null);
const selectedCompany = ref(null);

// Dados do lead
const leadData = ref({
  notes: '',
  stage: '',
  funnel_id: null,
  amount: '',
});

// Dropdowns state
const showFunnelDropdown = ref(false);
const showStageDropdown = ref(false);
const showInboxDropdown = ref(false);

// Caixa de onde a conversa nasce — define por qual canal/número o time fala com
// esse lead. Antes era escolhida sozinha (primeira caixa de API, ou a primeira
// da lista), o que em conta com vários números mandava a conversa pelo errado.
const selectedInboxId = ref(null);

const availableInboxes = computed(
  () => store.getters['inboxes/getInboxes'] || []
);

const selectedInbox = computed(() =>
  availableInboxes.value.find(inbox => inbox.id === selectedInboxId.value)
);

// Mantém o comportamento antigo como padrão: a caixa de API na frente, senão a
// primeira. Assim quem não se importa com o canal não precisa escolher nada.
const defaultInboxId = () => {
  const inboxes = availableInboxes.value;
  const preferida =
    inboxes.find(inbox => inbox.channel_type === 'Channel::Api') || inboxes[0];
  return preferida?.id || null;
};

// Máscara de moeda
const amountDisplay = ref('');

const formatCurrency = value => {
  if (!value && value !== 0) return '';
  const number =
    typeof value === 'string'
      ? parseFloat(value.replace(/\D/g, '')) / 100
      : value;
  if (Number.isNaN(number)) return '';
  return number.toLocaleString(browserLocale.value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const handleAmountInput = event => {
  let value = event.target.value.replace(/\D/g, '');
  if (value) {
    const number = parseFloat(value) / 100;
    leadData.value.amount = number;
    amountDisplay.value = formatCurrency(number);
  } else {
    leadData.value.amount = '';
    amountDisplay.value = '';
  }
};

// Computed para funil selecionado
const selectedFunnel = computed(() => {
  if (leadData.value.funnel_id) {
    return props.funnels.find(f => f.id === leadData.value.funnel_id);
  }
  return props.currentFunnel;
});

// Computed para estágios
const stagesOptions = computed(() => {
  if (!selectedFunnel.value?.stages) return [];
  return Object.entries(selectedFunnel.value.stages)
    .map(([key, config]) => ({
      key,
      label: config.label || key,
      color: config.color || '#718096',
      position: config.position || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

// Computed para estágio selecionado
const selectedStage = computed(() => {
  if (!leadData.value.stage) return stagesOptions.value[0] || null;
  return stagesOptions.value.find(s => s.key === leadData.value.stage);
});

// Validação: precisa ter pelo menos contato OU empresa, e funil
const isFormValid = computed(() => {
  const hasContactOrCompany = selectedContact.value || selectedCompany.value;
  return hasContactOrCompany && selectedFunnel.value;
});

// Watch para inicializar funil
watch(
  () => props.currentFunnel,
  funnel => {
    if (funnel && !leadData.value.funnel_id) {
      leadData.value.funnel_id = funnel.id;
    }
  },
  { immediate: true }
);

// Métodos
const resetForm = () => {
  searchQuery.value = '';
  searchResults.value = { contacts: [], companies: [] };
  showDropdown.value = false;
  showFunnelDropdown.value = false;
  showStageDropdown.value = false;
  showInboxDropdown.value = false;
  selectedInboxId.value = defaultInboxId();
  selectedContact.value = null;
  selectedCompany.value = null;
  amountDisplay.value = '';
  leadData.value = {
    notes: '',
    stage: '',
    funnel_id: props.currentFunnel?.id || null,
    amount: '',
  };
};

const selectInbox = inbox => {
  selectedInboxId.value = inbox.id;
  showInboxDropdown.value = false;
};

// Selecionar funil
const selectFunnel = funnel => {
  leadData.value.funnel_id = funnel.id;
  showFunnelDropdown.value = false;
  // Set default stage
  if (funnel.stages) {
    const stagesList = Object.entries(funnel.stages)
      .map(([key, config]) => ({ key, position: config.position || 0 }))
      .sort((a, b) => a.position - b.position);
    if (stagesList.length > 0) {
      leadData.value.stage = stagesList[0].key;
    }
  }
};

// Selecionar estágio
const selectStage = stage => {
  leadData.value.stage = stage.key;
  showStageDropdown.value = false;
};

// A lista de caixas pode estar vazia (o GET /inboxes falha calado). Carrega sem
// travar a abertura do modal, senão o seletor abre sem opção nenhuma e a
// conversa nasceria numa caixa que o usuário não escolheu.
const ensureInboxes = async () => {
  if (!availableInboxes.value.length) {
    await store.dispatch('inboxes/get');
  }
  if (!selectedInboxId.value) {
    selectedInboxId.value = defaultInboxId();
  }
};

const open = () => {
  resetForm();
  if (props.currentFunnel) {
    leadData.value.funnel_id = props.currentFunnel.id;
  }
  dialogRef.value?.open();
  ensureInboxes();
};

const close = () => {
  dialogRef.value?.close();
  emit('close');
};

// ===== BUSCA UNIFICADA =====
let searchTimeout = null;
const performSearch = () => {
  clearTimeout(searchTimeout);

  if (!searchQuery.value || searchQuery.value.length < 2) {
    searchResults.value = { contacts: [], companies: [] };
    showDropdown.value = false;
    return;
  }

  showDropdown.value = true;
  isSearching.value = true;

  searchTimeout = setTimeout(async () => {
    try {
      const query = searchQuery.value.trim();

      // Buscar contatos e empresas em paralelo
      const [contactsRes, companiesRes] = await Promise.all([
        ContactAPI.search(query, 1, 'name', '').catch(() => ({
          data: { payload: [] },
        })),
        CompanyAPI.search(query, 1, 'name').catch(() => ({
          data: { payload: [] },
        })),
      ]);

      searchResults.value = {
        contacts: contactsRes.data.payload || [],
        companies: companiesRes.data.payload || companiesRes.data || [],
      };
    } catch {
      searchResults.value = { contacts: [], companies: [] };
    } finally {
      isSearching.value = false;
    }
  }, 300);
};

// Selecionar contato
const selectContact = contact => {
  selectedContact.value = contact;
  showDropdown.value = false;
  searchQuery.value = '';

  // Se o contato tem empresa, vincular automaticamente
  if (contact.company) {
    selectedCompany.value = contact.company;
  }
};

// Selecionar empresa
const selectCompany = async company => {
  selectedCompany.value = company;
  showDropdown.value = false;
  searchQuery.value = '';

  // Buscar contatos da empresa
  try {
    const response = await ContactAPI.search('', 1, 'name', '', {
      company_id: company.id,
    });
    const companyContacts = response.data.payload || [];

    // Se empresa tem apenas 1 contato, selecionar automaticamente
    if (companyContacts.length === 1) {
      selectedContact.value = companyContacts[0];
    }
  } catch {
    // Sem problema se não conseguir buscar contatos
  }
};

const clearContact = () => {
  selectedContact.value = null;
};

const clearCompany = () => {
  selectedCompany.value = null;
  // Se limpar empresa, também limpa contato que veio dela
  if (selectedContact.value?.company?.id === selectedCompany.value?.id) {
    selectedContact.value = null;
  }
};

// Criar contato
const openCreateContactDialog = () => {
  showDropdown.value = false;
  createContactDialogRef.value?.dialogRef?.open();
};

const handleContactCreated = async contactData => {
  try {
    const response = await store.dispatch('contacts/create', contactData);
    if (response?.id) {
      selectContact(response);
      useAlert(t('NEW_LEAD.CONTACT_CREATED'));
    }
    createContactDialogRef.value?.onSuccess();
  } catch {
    useAlert(t('NEW_LEAD.ERROR_CREATING_CONTACT'));
  }
};

// Criar empresa
const openCreateCompanyDialog = () => {
  showDropdown.value = false;
  createCompanyDialogRef.value?.dialogRef?.open();
};

const handleCompanyCreated = async companyData => {
  try {
    await store.dispatch('companies/create', companyData);
    const companiesList = store.getters['companies/getCompaniesList'] || [];
    const newCompany = companiesList.find(c => c.name === companyData.name);
    if (newCompany) {
      selectCompany(newCompany);
      useAlert(t('NEW_LEAD.COMPANY_CREATED'));
    }
    createCompanyDialogRef.value?.onSuccess();
  } catch {
    useAlert(t('NEW_LEAD.ERROR_CREATING_COMPANY'));
  }
};

// Mapeamento já existente da conversa neste funil (o índice devolve array direto)
const findFunnelMapping = async (conversationId, funnelId) => {
  try {
    const { data } =
      await ConversationFunnelMappingsAPI.getMappings(conversationId);
    const mappings = Array.isArray(data) ? data : data?.mappings || [];
    return mappings.find(m => String(m.funnel_id) === String(funnelId)) || null;
  } catch {
    return null;
  }
};

// Extrai a mensagem que o backend devolveu; sem isso todo 422 vira o genérico
// "Erro ao criar lead" e não dá para saber o que travou.
const backendErrorMessage = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.response?.data?.errors?.join?.(', ') ||
  null;

// ===== CRIAR LEAD =====
const createNewLead = async () => {
  if (!isFormValid.value) {
    useAlert(t('NEW_LEAD.VALIDATION_ERROR'));
    return;
  }

  const targetFunnel = selectedFunnel.value;
  isCreatingLead.value = true;

  try {
    let contactForConversation = selectedContact.value;

    // Se não tem contato mas tem empresa, criar contato para a empresa
    if (!contactForConversation && selectedCompany.value) {
      const companyContactData = {
        name: selectedCompany.value.name,
        phone_number: selectedCompany.value.phone || '',
        email: selectedCompany.value.email || '',
        company_id: selectedCompany.value.id,
        additional_attributes: {
          source: 'company_lead',
        },
      };

      const newContact = await store.dispatch(
        'contacts/create',
        companyContactData
      );
      if (newContact?.id) {
        contactForConversation = newContact;
      } else {
        throw new Error('Failed to create contact');
      }
    }

    // Se contato selecionado pertence a empresa diferente, atualizar
    if (
      selectedCompany.value &&
      contactForConversation &&
      contactForConversation.company?.id !== selectedCompany.value.id
    ) {
      await ContactAPI.update(contactForConversation.id, {
        company_id: selectedCompany.value.id,
      });
    }

    // A store pode estar vazia por falha silenciosa do GET /inboxes (a action
    // engole o erro), então tenta recarregar antes de desistir — senão o lead
    // morre com "nenhum canal" numa conta que TEM canal.
    if (!availableInboxes.value.length) {
      await store.dispatch('inboxes/get');
    }
    // A caixa escolhida manda. Só cai no padrão se o modal abriu antes de a
    // lista carregar e ninguém chegou a escolher.
    const targetInbox =
      selectedInbox.value ||
      availableInboxes.value.find(inbox => inbox.id === defaultInboxId());

    if (!targetInbox) {
      useAlert(t('NEW_LEAD.NO_INBOX_ERROR'));
      return;
    }

    // Criar conversa via API ou usar existente
    const conversationPayload = {
      contact_id: contactForConversation.id,
      inbox_id: targetInbox.id,
      status: 'open',
      additional_attributes: {
        source: 'manual_lead',
        notes: leadData.value.notes || '',
        company_id: selectedCompany.value?.id,
      },
    };

    let conversation;
    try {
      const response = await ConversationAPI.create(conversationPayload);
      conversation = response.data;
    } catch (convError) {
      // Se já existe conversa aberta, usar ela
      if (
        convError.response?.status === 422 &&
        convError.response?.data?.conversation_id
      ) {
        const existingConvId = convError.response.data.conversation_id;
        // Buscar conversa existente
        const existingConvResponse = await ConversationAPI.show(existingConvId);
        conversation = existingConvResponse.data;
      } else {
        throw convError;
      }
    }

    // Adicionar ao funil. Usa a etapa que está VISÍVEL no modal: sem isso o
    // fallback pegava Object.keys(stages)[0] (ordem do hash), que não é
    // necessariamente a primeira etapa por posição.
    const stageName =
      leadData.value.stage ||
      selectedStage.value?.key ||
      Object.keys(targetFunnel.stages)[0];

    let existingMapping = null;
    try {
      await ConversationFunnelMappingsAPI.addToFunnel(
        conversation.id,
        targetFunnel.id,
        stageName
      );
    } catch (mappingError) {
      // A conversa reaproveitada já podia estar neste funil (o backend responde
      // 422 "já está associada"). Isso não é falha: é o mesmo lead, o card já
      // existe no board. NÃO move de etapa: o contato pode estar em negociação
      // e a etapa escolhida aqui é só o default do modal — mover jogaria o lead
      // pra trás no funil. Avisa onde ele está e segue.
      existingMapping =
        mappingError.response?.status === 422
          ? await findFunnelMapping(conversation.id, targetFunnel.id)
          : null;

      if (!existingMapping) throw mappingError;
    }

    // Etapa efetiva do card: a que já existe vence a escolhida no modal.
    const effectiveStage = existingMapping?.stage_name || stageName;

    // Criar Deal para integração com relatórios CRM
    const stageConfig = targetFunnel.stages[effectiveStage] || {};

    // owner_id NÃO vai no payload: o backend respeita o toggle
    // funnel.auto_assign_owner. Padrão OFF = card nasce sem responsável,
    // alguém atribui depois. Enviar current_user aqui furava esse contrato.
    const dealPayload = {
      name:
        contactForConversation.name ||
        selectedCompany.value?.name ||
        t('NEW_LEAD.DEFAULT_DEAL_NAME'),
      conversation_id: conversation.id,
      funnel_id: targetFunnel.id,
      // Etapa efetiva: Deal#sync_to_pipeline reescreve a etapa do card quando
      // difere, então mandar a etapa do modal aqui moveria o lead pra trás.
      stage_name: effectiveStage,
      contact_id: contactForConversation.id,
      company_id: selectedCompany.value?.id || null,
      amount: leadData.value.amount || 0,
      probability: stageConfig.probability || 0,
      currency: currency.value,
      status: 'open',
      notes: leadData.value.notes || '',
    };

    try {
      // Conversa reaproveitada pode já ter Negócio: criar outro duplicaria o
      // card nos relatórios a cada clique repetido no "Criar Lead".
      const hasDeal =
        !!existingMapping &&
        (await DealAPI.get({
          conversation_id: conversation.id,
          funnel_id: targetFunnel.id,
        })
          .then(({ data }) => (data?.payload || []).length > 0)
          .catch(() => false));

      if (!hasDeal) await DealAPI.create(dealPayload);
    } catch (dealError) {
      // Log para debug, mas não impedir a criação do lead
      // eslint-disable-next-line no-console
      console.warn('Deal creation failed:', dealError);
    }

    useAlert(
      existingMapping
        ? t('NEW_LEAD.ALREADY_IN_FUNNEL', {
            stage:
              existingMapping.stage_label || existingMapping.stage_name || '',
          })
        : t('NEW_LEAD.SUCCESS')
    );
    emit('created', conversation);
    close();
  } catch (error) {
    useAlert(backendErrorMessage(error) || t('NEW_LEAD.ERROR'));
  } finally {
    isCreatingLead.value = false;
  }
};

defineExpose({ open, close, dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('NEW_LEAD.TITLE')"
    width="lg"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="resetForm"
  >
    <div class="flex flex-col gap-5">
      <!-- Seleções atuais -->
      <div
        v-if="selectedContact || selectedCompany"
        class="flex flex-col gap-3"
      >
        <!-- Contato selecionado -->
        <div
          v-if="selectedContact"
          class="flex items-center justify-between p-3 rounded-lg border border-n-teal-6 bg-n-teal-2"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-full bg-n-teal-3 text-n-teal-11"
            >
              <Icon icon="i-lucide-user" class="size-5" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ selectedContact.name || t('NEW_LEAD.NO_NAME') }}
              </p>
              <p class="text-xs text-n-slate-11 truncate">
                {{
                  selectedContact.phone_number || selectedContact.email || '-'
                }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="p-1.5 rounded-md hover:bg-n-teal-3 text-n-teal-11"
            @click="clearContact"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>

        <!-- Empresa selecionada -->
        <div
          v-if="selectedCompany"
          class="flex items-center justify-between p-3 rounded-lg border border-n-blue-6 bg-n-blue-2"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-full bg-n-blue-3 text-n-blue-11"
            >
              <Icon icon="i-lucide-building-2" class="size-5" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ selectedCompany.name }}
              </p>
              <p
                v-if="selectedCompany.phone || selectedCompany.email"
                class="text-xs text-n-slate-11 truncate"
              >
                {{ selectedCompany.phone || selectedCompany.email }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="p-1.5 rounded-md hover:bg-n-blue-3 text-n-blue-11"
            @click="clearCompany"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
      </div>

      <!-- Campo de Busca Unificada -->
      <section class="space-y-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('NEW_LEAD.SEARCH_LABEL') }}
        </label>

        <div class="relative">
          <Input
            v-model="searchQuery"
            type="text"
            :placeholder="t('NEW_LEAD.SEARCH_PLACEHOLDER')"
            @input="performSearch"
            @focus="showDropdown = searchQuery.length >= 2"
          />

          <!-- Dropdown de resultados -->
          <div
            v-if="showDropdown"
            class="absolute z-50 w-full mt-1 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-80 overflow-y-auto"
          >
            <!-- Loading -->
            <div
              v-if="isSearching"
              class="px-3 py-4 text-sm text-n-slate-11 text-center"
            >
              <Icon
                icon="i-lucide-loader-2"
                class="size-4 animate-spin inline mr-2"
              />
              {{ t('NEW_LEAD.SEARCHING') }}
            </div>

            <template v-else>
              <!-- Contatos -->
              <div v-if="searchResults.contacts.length > 0">
                <div
                  class="px-3 py-1.5 text-xs font-medium text-n-slate-10 bg-n-solid-2 sticky top-0"
                >
                  {{ t('NEW_LEAD.CONTACTS') }}
                </div>
                <button
                  v-for="contact in searchResults.contacts"
                  :key="`contact-${contact.id}`"
                  type="button"
                  class="w-full px-3 py-2 text-left hover:bg-n-solid-3 flex items-center gap-3"
                  @click="selectContact(contact)"
                >
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full bg-n-teal-2 text-n-teal-11 flex-shrink-0"
                  >
                    <Icon icon="i-lucide-user" class="size-4" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-n-slate-12 truncate">
                      {{ contact.name || t('NEW_LEAD.NO_NAME') }}
                    </p>
                    <p class="text-xs text-n-slate-10 truncate">
                      {{ contact.phone_number || contact.email || '-' }}
                      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                      <span v-if="contact.company" class="ml-1 text-n-blue-10">
                        • {{ contact.company.name }}
                      </span>
                    </p>
                  </div>
                </button>
              </div>

              <!-- Empresas -->
              <div v-if="searchResults.companies.length > 0">
                <div
                  class="px-3 py-1.5 text-xs font-medium text-n-slate-10 bg-n-solid-2 sticky top-0"
                >
                  {{ t('NEW_LEAD.COMPANIES') }}
                </div>
                <button
                  v-for="company in searchResults.companies"
                  :key="`company-${company.id}`"
                  type="button"
                  class="w-full px-3 py-2 text-left hover:bg-n-solid-3 flex items-center gap-3"
                  @click="selectCompany(company)"
                >
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full bg-n-blue-2 text-n-blue-11 flex-shrink-0"
                  >
                    <Icon icon="i-lucide-building-2" class="size-4" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-n-slate-12 truncate">
                      {{ company.name }}
                    </p>
                    <p
                      v-if="company.phone || company.email"
                      class="text-xs text-n-slate-10 truncate"
                    >
                      {{ company.phone || company.email }}
                    </p>
                  </div>
                </button>
              </div>

              <!-- Sem resultados -->
              <div
                v-if="
                  searchResults.contacts.length === 0 &&
                  searchResults.companies.length === 0 &&
                  searchQuery.length >= 2
                "
                class="px-3 py-3 text-sm text-n-slate-11 text-center"
              >
                {{ t('NEW_LEAD.NO_RESULTS') }}
              </div>

              <!-- Criar novo -->
              <div class="border-t border-n-weak">
                <button
                  type="button"
                  class="w-full px-3 py-2.5 text-left hover:bg-n-teal-2 flex items-center gap-2 text-n-teal-text"
                  @click="openCreateContactDialog"
                >
                  <Icon icon="i-lucide-user-plus" class="size-4" />
                  <span class="text-sm font-medium">{{
                    t('NEW_LEAD.CREATE_CONTACT')
                  }}</span>
                </button>
                <button
                  type="button"
                  class="w-full px-3 py-2.5 text-left hover:bg-n-blue-2 flex items-center gap-2 text-n-blue-text"
                  @click="openCreateCompanyDialog"
                >
                  <Icon icon="i-lucide-building-2" class="size-4" />
                  <span class="text-sm font-medium">{{
                    t('NEW_LEAD.CREATE_COMPANY')
                  }}</span>
                </button>
              </div>
            </template>
          </div>
        </div>

        <!-- Helper text -->
        <p class="text-xs text-n-slate-10">
          {{ t('NEW_LEAD.SEARCH_HELPER') }}
        </p>
      </section>

      <!-- Caixa de entrada: por qual canal a conversa nasce -->
      <section class="space-y-4 border-t border-n-weak pt-4">
        <div class="relative">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('NEW_LEAD.INBOX') }}
          </label>
          <button
            type="button"
            class="w-full px-3 py-2 text-left border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 hover:bg-n-alpha-3"
            @click="showInboxDropdown = !showInboxDropdown"
          >
            <span v-if="selectedInbox">{{ selectedInbox.name }}</span>
            <span v-else class="text-n-slate-10">
              {{ t('NEW_LEAD.SELECT') }}
            </span>
          </button>
          <div
            v-if="showInboxDropdown && availableInboxes.length > 0"
            class="absolute z-20 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
          >
            <button
              v-for="inbox in availableInboxes"
              :key="inbox.id"
              type="button"
              class="w-full px-3 py-2 text-left text-n-slate-12 hover:bg-n-alpha-3 first:rounded-t-lg last:rounded-b-lg"
              @click="selectInbox(inbox)"
            >
              {{ inbox.name }}
            </button>
          </div>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('NEW_LEAD.INBOX_HINT') }}
          </p>
        </div>
      </section>

      <!-- Funil e Etapa -->
      <section class="space-y-4 border-t border-n-weak pt-4">
        <div class="grid grid-cols-2 gap-4">
          <!-- Funil -->
          <div class="relative">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('NEW_LEAD.FUNNEL_REQUIRED') }}
            </label>
            <button
              type="button"
              class="w-full px-3 py-2 text-left border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 hover:bg-n-alpha-3"
              @click="showFunnelDropdown = !showFunnelDropdown"
            >
              {{ selectedFunnel?.name || t('NEW_LEAD.SELECT') }}
            </button>
            <div
              v-if="showFunnelDropdown"
              class="absolute z-20 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              <button
                v-for="funnel in funnels"
                :key="funnel.id"
                type="button"
                class="w-full px-3 py-2 text-left text-n-slate-12 hover:bg-n-alpha-3 first:rounded-t-lg last:rounded-b-lg"
                @click="selectFunnel(funnel)"
              >
                {{ funnel.name }}
              </button>
              <div
                v-if="funnels.length === 0"
                class="px-3 py-2 text-sm text-n-slate-11 text-center"
              >
                {{ t('NEW_LEAD.NO_FUNNELS') }}
              </div>
            </div>
          </div>

          <!-- Etapa -->
          <div class="relative">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('NEW_LEAD.STAGE') }}
            </label>
            <button
              type="button"
              class="w-full px-3 py-2 text-left border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12 hover:bg-n-alpha-3 disabled:opacity-50"
              :disabled="!selectedFunnel"
              @click="showStageDropdown = !showStageDropdown"
            >
              <span v-if="selectedStage" class="flex items-center gap-2">
                <span
                  class="w-2 h-2 rounded-full"
                  :style="{ backgroundColor: selectedStage.color }"
                />
                {{ selectedStage.label }}
              </span>
              <span v-else class="text-n-slate-10">
                {{ t('NEW_LEAD.SELECT') }}
              </span>
            </button>
            <div
              v-if="showStageDropdown && stagesOptions.length > 0"
              class="absolute z-20 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              <button
                v-for="stage in stagesOptions"
                :key="stage.key"
                type="button"
                class="w-full px-3 py-2 text-left text-n-slate-12 hover:bg-n-alpha-3 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
                @click="selectStage(stage)"
              >
                <span
                  class="w-2 h-2 rounded-full"
                  :style="{ backgroundColor: stage.color }"
                />
                {{ stage.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Valor -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('NEW_LEAD.AMOUNT') }}
          </label>
          <div class="flex items-center gap-2">
            <span class="text-sm text-n-slate-11 flex-shrink-0">
              {{ currencySymbol }}
            </span>
            <input
              :value="amountDisplay"
              type="text"
              inputmode="numeric"
              :placeholder="t('NEW_LEAD.AMOUNT_PLACEHOLDER')"
              class="flex-1 h-10 px-3 rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:outline-n-brand hover:outline-n-slate-6 border-0"
              @input="handleAmountInput"
            />
          </div>
        </div>
      </section>

      <!-- Observações -->
      <section>
        <TextArea
          v-model="leadData.notes"
          :label="t('NEW_LEAD.NOTES')"
          :placeholder="t('NEW_LEAD.NOTES_PLACEHOLDER')"
          rows="2"
        />
      </section>

      <!-- Botões -->
      <div class="flex justify-end gap-2 pt-3 border-t border-n-weak">
        <Button variant="faded" color="slate" @click="close">
          {{ t('NEW_LEAD.CANCEL') }}
        </Button>
        <Button
          color="blue"
          :is-loading="isCreatingLead"
          :disabled="!isFormValid"
          @click="createNewLead"
        >
          <Icon icon="i-lucide-plus" class="size-4 mr-1" />
          {{ t('NEW_LEAD.CREATE') }}
        </Button>
      </div>
    </div>

    <!-- Dialog de criar contato -->
    <CreateNewContactDialog
      ref="createContactDialogRef"
      @create="handleContactCreated"
    />

    <!-- Dialog de criar empresa -->
    <CreateNewCompanyDialog
      ref="createCompanyDialogRef"
      @create="handleCompanyCreated"
    />
  </Dialog>
</template>
